import { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import SignIn from '../components/SignIn';
import SignUp from '../components/SignUp';
import Home from '../components/Home';
import HomeNew from '../components/HomeNew';
import Footer from '../components/Footer';
import RedirectRoute from '../components/RedirectRoute';
import Welcome from '../components/Welcome';
import ProfileNew from '../components/ProfileNew';
import ThankYou from '../components/ThankYou';
import VerifyEmail from '../components/VerifyEmail';
import ResetPassword from '../components/ResetPassword';
import ResetPasswordMessage from '../components/ResetPasswordMessage';
import InputDescriptions from '../components/InputDescriptions';
import ResultsDisplay from '../components/ResultsDisplay';
import ProtectedRoute from '../components/ProtectedRoute';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import FAQPage from './FAQPage';
import { useAuth } from '../hooks/useAuth';
import { usePayments } from '../hooks/usePayments';
import { useIsMobile } from "../hooks/useIsMobile";
import STATIC_PRODUCTS from '../data/staticProducts';



function MainPage({handleThemeChange, landingComponent}) {
    const { user, logOut } = useAuth();
    const { getCurrentPlan, getCustomer, getProducts } = usePayments();
    const location = useLocation();
    const [plans, setPlans] = useState([])
    const [selectedPlan, setSelectedPlan] = useState('');
    const [formStep, setFormStep] = useState(0);
    const [results, setResults] = useState({});
    const isMobile = useIsMobile()

    useEffect(() => {
        if(user?.uid){
            Promise.all([
                getCustomer(user?.uid),
                getCurrentPlan(user?.uid),
            ]);
        }
    }, [user?.uid, getCurrentPlan, user?.email, getCustomer])

    useEffect(() => {
        const fetchProducts = async (retries = 2) => {
            for (let i = 0; i < retries; i++) {
                let products = await getProducts();
                if (products && products.length > 0) {
                    setPlans(products);
                    if (products[0].prices?.priceId) {
                        setSelectedPlan(products[0].prices.priceId);
                    }
                    return;
                }
                // Wait before retry
                if (i < retries - 1) {
                    await new Promise(r => setTimeout(r, 1500));
                }
            }
            // API failed - use static fallback (works even with ad blockers)
            console.warn('API failed, using static products fallback');
            setPlans(STATIC_PRODUCTS);
            if (STATIC_PRODUCTS[0]?.prices?.priceId) {
                setSelectedPlan(STATIC_PRODUCTS[0].prices.priceId);
            }
        }
        fetchProducts()
    }, [getProducts])
    
    // Routes that have their own header (don't show global header for these)
    const routesWithOwnHeader = ['/signin', '/signup', '/welcome', '/reset', '/resetsent', '/thankyou', '/privacy', '/terms'];
    const hasOwnHeader = routesWithOwnHeader.includes(location.pathname);
  
    return (
        <div>
            {/* Only show Header for authenticated users on routes that don't have their own header */}
            {user && !hasOwnHeader && <Header 
                user={user}
                logOut={logOut} 
                handleThemeChange={handleThemeChange} 
                setFormStep={setFormStep}
            />}
            <Routes>
                <Route path="/signin" element={<><Header user={user} logOut={logOut} handleThemeChange={handleThemeChange} setFormStep={setFormStep}/><RedirectRoute><SignIn/></RedirectRoute><Footer/></>} />
                <Route path="/signup" element={<><Header user={user} logOut={logOut} handleThemeChange={handleThemeChange} setFormStep={setFormStep}/><RedirectRoute><SignUp plans={plans} selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan}/></RedirectRoute><Footer/></>} />
                <Route path="/welcome" element={<><Header user={user} logOut={logOut} handleThemeChange={handleThemeChange} setFormStep={setFormStep}/><Welcome/><Footer/></>} />
                <Route path="/reset" element={<><Header user={user} logOut={logOut} handleThemeChange={handleThemeChange} setFormStep={setFormStep}/><ResetPassword/><Footer/></>} />
                <Route path="/resetsent" element={<><Header user={user} logOut={logOut} handleThemeChange={handleThemeChange} setFormStep={setFormStep}/><ResetPasswordMessage/><Footer/></>} />
                <Route path="/thankyou" element={<><Header user={user} logOut={logOut} handleThemeChange={handleThemeChange} setFormStep={setFormStep}/><RedirectRoute><ThankYou/></RedirectRoute><Footer/></>}/>
                <Route path="/privacy" element={<><Header user={user} logOut={logOut} handleThemeChange={handleThemeChange} setFormStep={setFormStep}/><PrivacyPolicy/><Footer/></>}/>
                <Route path="/terms" element={<><Header user={user} logOut={logOut} handleThemeChange={handleThemeChange} setFormStep={setFormStep}/><TermsOfService/><Footer/></>}/>
                <Route path="/verifyemail" element={<><RedirectRoute><VerifyEmail/></RedirectRoute><Footer/></>}/>
                <Route path="/" element={landingComponent ? landingComponent : <ProtectedRoute><HomeNew user={user} setResults={setResults} isMobile={isMobile}/></ProtectedRoute>} />
                <Route path="/old" element={<ProtectedRoute> <Home user={user} setResults={setResults} formStep={formStep} setFormStep={setFormStep} isMobile={isMobile}/></ProtectedRoute>} />
                <Route path="/descriptions" element={<ProtectedRoute> <InputDescriptions /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><ProfileNew plans={plans} selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan}/></ProtectedRoute>} />
                <Route path="/faq" element={<ProtectedRoute><FAQPage/></ProtectedRoute>} />
                <Route path="/results" element={<ProtectedRoute><ResultsDisplay
                    results={results}
                    amortization={results.amortization_schedule || []}
                    cashFlow={results.cash_flow || []}
                    valuation={results.valuation || {}}
                    passed_properties={results.passed_properties || []}
                    passed_properties_count={results.passed_properties_count || 0}
                    total_properties_count={results.total_properties_count || 0}
                    annual_irrs={results.annual_irrs || []}
                    coc={results.coc || []}
                    isMobile={isMobile} 
                /></ProtectedRoute>}/>
            </Routes>
            {user && <Footer/>}
        </div>
    )   
}

export default MainPage; 